/**
 * Every shortcut the workspace binds, in one table.
 *
 * One table because a keyboard is a contract: the palette shows these keys, the help sheet lists
 * them, and the handlers answer them. Three copies of "Alt+H is hold" would be three places to
 * disagree, and the one that disagreed would be the one somebody pressed during a call.
 *
 * **Scopes** are what makes that possible without a global registry of handlers. A scope is the
 * situation a shortcut belongs to — the whole application, a call in progress, a board — and a
 * handler only ever asks for the scope it is in. Two scopes may use the same letter: `Alt+T` is a
 * transfer on a call and nothing anywhere else, so the call screen binds it and no other screen has
 * to know.
 *
 * ## Why the modifiers are what they are
 *
 * - **`mod`** — <kbd>Ctrl</kbd>, or <kbd>Cmd</kbd> on a Mac — is for the things every application
 *   has: the palette. People already know <kbd>Ctrl</kbd>+<kbd>K</kbd>, and a product that chose
 *   differently would be teaching an exception for no benefit.
 * - **`alt`** is for everything in a working screen. A bare letter is unusable where there are text
 *   fields: a bare `h` would hang up a call while somebody typed "hold on". `mod` with a letter
 *   belongs to the browser and the operating system — <kbd>Ctrl</kbd>+<kbd>T</kbd> opens a tab,
 *   <kbd>Cmd</kbd>+<kbd>H</kbd> hides the window — and a page that fought them would lose and look
 *   broken doing it. <kbd>Alt</kbd> is almost entirely free in a browser, and it is the one modifier
 *   a screen-reader user can still reach: it is not the key NVDA or VoiceOver reserves.
 *
 * Nothing here is a single key on its own. A workspace where a letter does something is a workspace
 * that cannot be typed in.
 */

/** The situations a shortcut can belong to. */
export const shortcutScopes = ['global', 'call', 'board'] as const;
export type ShortcutScope = (typeof shortcutScopes)[number];

export interface ShortcutDefinition {
  /** Stable id, in `<scope>.<action>` form, used as the handler's key and the i18n suffix. */
  readonly id: string;
  readonly scope: ShortcutScope;
  /** The letter, lower case. Compared against `event.key.toLowerCase()`. */
  readonly key: string;
  /** `mod` is Ctrl, or Cmd on a Mac; `alt` is Alt, or Option. */
  readonly modifier: 'mod' | 'alt';
  /** What it does, as an i18n key in the `common` namespace. */
  readonly labelKey: string;
}

/**
 * The map.
 *
 * Adding a row is how a shortcut comes into existence: the screen that owns the action passes a
 * handler under the same id, and the help sheet and the palette pick the row up without being
 * touched. A row with no handler on screen is simply not bound, which is why there is no shortcut
 * anywhere that listens and does nothing.
 */
export const shortcuts: readonly ShortcutDefinition[] = [
  {
    id: 'global.palette',
    scope: 'global',
    key: 'k',
    modifier: 'mod',
    labelKey: 'shortcut.palette',
  },
  { id: 'call.answer', scope: 'call', key: 'a', modifier: 'alt', labelKey: 'shortcut.answer' },
  // `e` for end, because `h` is already hold: a keyboard where two actions share a letter is a
  // keyboard somebody will press wrong while a caller is waiting.
  { id: 'call.hangUp', scope: 'call', key: 'e', modifier: 'alt', labelKey: 'shortcut.hangUp' },
  { id: 'call.hold', scope: 'call', key: 'h', modifier: 'alt', labelKey: 'shortcut.hold' },
  { id: 'call.transfer', scope: 'call', key: 't', modifier: 'alt', labelKey: 'shortcut.transfer' },
  {
    id: 'call.disposition',
    scope: 'call',
    key: 'd',
    modifier: 'alt',
    labelKey: 'shortcut.disposition',
  },
  // The boards: a supervisor watching a queue fill up should be able to get to the agent board and
  // back without reaching for the mouse.
  { id: 'board.queues', scope: 'board', key: 'q', modifier: 'alt', labelKey: 'shortcut.queues' },
  { id: 'board.agents', scope: 'board', key: 'g', modifier: 'alt', labelKey: 'shortcut.agents' },
];

/** The rows of one scope, for a help sheet or a palette group. */
export function shortcutsIn(scope: ShortcutScope): readonly ShortcutDefinition[] {
  return shortcuts.filter((shortcut) => shortcut.scope === scope);
}

export function shortcutById(id: string): ShortcutDefinition | undefined {
  return shortcuts.find((shortcut) => shortcut.id === id);
}

/** What a key press has to look like. Only the fields that decide, so a test needs no DOM. */
export interface ShortcutEvent {
  readonly key: string;
  readonly altKey: boolean;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
}

/**
 * The shortcut a key press asks for within one scope, or nothing.
 *
 * A scope is required rather than optional: a handler that matched every scope would answer another
 * screen's keys, and the bug would be a control acting on something not on screen.
 *
 * The modifier has to match **exactly**. `Ctrl+Alt+T` is a terminal on one desktop and an accent on
 * another, so an `alt` shortcut refuses `ctrl` and `meta`, and a `mod` shortcut refuses `alt`.
 */
export function matchShortcut(
  event: ShortcutEvent,
  scope: ShortcutScope,
): ShortcutDefinition | undefined {
  const pressed = event.key.toLowerCase();
  const mod = event.ctrlKey || event.metaKey;
  return shortcuts.find(
    (shortcut) =>
      shortcut.scope === scope &&
      shortcut.key === pressed &&
      (shortcut.modifier === 'mod'
        ? mod && !event.altKey
        : event.altKey && !event.ctrlKey && !event.metaKey),
  );
}

/**
 * Whether the person is typing, in which case the keyboard is theirs.
 *
 * `Alt` with a letter inserts a character on some layouts — on a Mac, `Alt+e` begins an accent — so
 * a shortcut that fired inside a notes box would both act on the call and eat the keystroke.
 *
 * The palette's own `mod` shortcut is the exception and deliberately does not consult this: being
 * able to open it from inside a search field is the point of having it.
 */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (target === null || !(target instanceof globalThis.HTMLElement)) {
    return false;
  }
  if (target.isContentEditable) {
    return true;
  }
  return ['input', 'textarea', 'select'].includes(target.tagName.toLowerCase());
}

/**
 * How a shortcut is written where somebody reads it.
 *
 * `Cmd` on a Mac and `Ctrl` everywhere else, decided from the platform the page is actually on
 * rather than from a preference: the key the person has to press is a fact about their keyboard.
 */
export function shortcutLabel(
  shortcut: ShortcutDefinition,
  platform: string = globalThis.navigator?.platform ?? '',
): string {
  const mac = /mac|iphone|ipad/i.test(platform);
  const modifier = shortcut.modifier === 'mod' ? (mac ? '⌘' : 'Ctrl') : mac ? '⌥' : 'Alt';
  return `${modifier} + ${shortcut.key.toUpperCase()}`;
}
